import {alpha, Grid, Link, Skeleton} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {RootState, useAppSelector} from "../../../store";
import {getRandomNumber} from "../../../util/utils";
import {useEffect, useMemo, useRef, useState} from "react";
import {RoutePaths} from "../../../util/constants";
import {Autoplay, EffectFade} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Swiper as SwiperType} from "swiper";
import {useTheme} from "@mui/material/styles";
import {Link as RouterLink} from "react-router-dom";

const SloganCarousel = () => {
    const homeStaticInfo = useAppSelector((state: RootState) => state.homeReducer.content)
    const homeStaticInfoLoading = useAppSelector((state: RootState) => state.homeReducer.loading)
    const {t, i18n} = useTranslation()
    const [activeCarouselIndex, setActiveCarouselIndex] = useState<undefined | number>(undefined)
    const swiperRef = useRef<SwiperType>(null)
    const theme = useTheme()

    const slides = useMemo(() => homeStaticInfo.carousel.length === 2
        ? [...homeStaticInfo.carousel, ...homeStaticInfo.carousel]
        : homeStaticInfo.carousel, [homeStaticInfo.carousel])

    useEffect(() => {
        if (Boolean(slides.length)) {
            setActiveCarouselIndex(0)
        }
    }, [slides.length]);

    const handleCarouselChange = (swiper: any) => {
        if (slides.length && !isNaN(swiper.realIndex)) {
            setActiveCarouselIndex(swiper.realIndex)
        }
    }

    const getCarouselText = () => {
        if (homeStaticInfoLoading) {
            return <>
                <Skeleton animation="wave"/>
                <Skeleton animation="wave"/>
                <Skeleton animation="wave" width={`${getRandomNumber(20, 60)}%`}/>
            </>
        }
        if (activeCarouselIndex === undefined) {
            return null
        }

        return slides[activeCarouselIndex][`text_${i18n.language}`]
    }

    const seeMoreLink = useMemo(() => {
        if (activeCarouselIndex === undefined) {
            return ""
        }

        return slides[activeCarouselIndex].link
    }, [activeCarouselIndex, slides])

    return (
        <Grid container sx={{minHeight: '300px', boxShadow: 5}}>
            <Grid container item xs={12} sm={7} md={8} alignItems="center" p={4} sx={{
                backgroundColor: alpha(theme.palette.primary.main, .05),
                backdropFilter: "blur(5px)"
            }}>
                <Box width="100%">
                    <Typography variant='h3' fontWeight="bold" color="primary"
                                sx={{fontSize: {xs: '2.5rem', md: '3rem'}}}>
                        {
                            homeStaticInfoLoading ? <>
                                    <Skeleton animation="wave"/>
                                    <Skeleton animation="wave"/>
                                </> :
                                homeStaticInfo[`slogan_${i18n.language}`]
                        }
                    </Typography>
                    <Box textAlign="right" mt={2}>
                        <Button variant='contained' component={RouterLink}
                                to={RoutePaths.WISHES} size="large">{t('lets_begin')}</Button>
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12} sm={5} md={4} p={4} sx={{
                backgroundColor: alpha(theme.palette.primary.main, .15),
                backdropFilter: "blur(5px)",
                minHeight: "300px",
                transition: "background-color 0.2s ease",
                "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, .2),
                },
            }}
                  onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
                  onMouseLeave={() => swiperRef.current?.autoplay?.start()}
                  component={Link} href={seeMoreLink} underline="none"
            >
                <Typography variant="h6" color="primary" mb={6}>
                    {getCarouselText()}
                </Typography>

                {homeStaticInfoLoading ?
                    <Skeleton animation='pulse' variant='rectangular' height={250} width="84%" sx={{
                        position: 'absolute',
                        width: '100%',
                        left: "8%",
                        paddingInline: '32px',
                    }}/> :
                    <Swiper
                        onSwiper={(swiper: SwiperType) => {
                            // @ts-ignore
                            swiperRef.current = swiper
                        }}
                        modules={[Autoplay, EffectFade]}
                        spaceBetween={50}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        autoplay={{
                            delay: 3000,
                            disableOnInteraction: true,
                        }}
                        effect="fade"
                        fadeEffect={{crossFade: true}}
                        loop={slides.length > 1}
                        onSlideChange={handleCarouselChange}
                        speed={1500}
                        style={{
                            position: 'absolute',
                            width: '100%',
                            left: 0,
                            paddingInline: '32px',
                            marginTop: '8px',
                        }}
                    >
                        {slides.map((c, idx) => (
                            <SwiperSlide key={c.text_en + idx}>
                                <Box width="100%">
                                    <img
                                        key={c.text_en}
                                        width="100%"
                                        height="100%"
                                        alt={c.text_en}
                                        src={c.image}
                                        style={{objectFit: 'contain'}}
                                    />
                                </Box>
                            </SwiperSlide>
                        ))}
                    </Swiper>}
            </Grid>
        </Grid>
    )
}

export default SloganCarousel
